import React, { useState, useEffect, useRef } from 'react';
import { AppSettings, AudioDevice, CameraDevice, CaptureSource, QualityConfig, QualityPreset } from '@/types';
import { recordingEngine } from '@/services/recordingEngine';
import { audioService } from '@/services/audioService';
import { AudioMeter } from '@/components/AudioMeter';
import { GoogleService, GoogleMeetEvent } from '@/services/googleService';
import {
  Monitor,
  AppWindow,
  Mic,
  Volume2,
  HardDrive,
  Settings2,
  Video,
  Play,
  CheckCircle2,
  AlertTriangle,
  Folder,
  RefreshCw,
  Camera,
  Layers,
  Maximize2,
  ExternalLink,
  Calendar,
  Users,
  Sparkles,
  Globe,
  Plus,
  Trash2,
  X,
  Clock
} from 'lucide-react';

interface RecordingStudioScreenProps {
  settings: AppSettings;
  onStartRecording: (source: CaptureSource | null, quality: QualityConfig, cameraStream?: MediaStream | null) => void;
  availableDiskSpaceGb: number;
  initialTab?: 'screen' | 'window' | 'camera';
}

const QUALITY_PRESETS: Record<QualityPreset, QualityConfig> = {
  '1080p60': { preset: '1080p60', width: 1920, height: 1080, fps: 60, bitrateBps: 20000000, label: '1080p @ 60fps (Ultra Smooth • 20 Mbps)' },
  '1080p30': { preset: '1080p30', width: 1920, height: 1080, fps: 30, bitrateBps: 15000000, label: '1080p @ 30fps (Crystal Clear • 15 Mbps)' },
  '720p30': { preset: '720p30', width: 1280, height: 720, fps: 30, bitrateBps: 8000000, label: '720p @ 30fps (High Definition • 8 Mbps)' },
  '4k30': { preset: '4k30', width: 3840, height: 2160, fps: 30, bitrateBps: 40000000, label: '4K UHD @ 30fps (Pristine Studio • 40 Mbps)' },
};

export const RecordingStudioScreen: React.FC<RecordingStudioScreenProps> = ({
  settings,
  onStartRecording,
  availableDiskSpaceGb,
  initialTab
}) => {
  const [sources, setSources] = useState<CaptureSource[]>([]);
  const [selectedSource, setSelectedSource] = useState<CaptureSource | null>(null);
  const [sourceTypeTab, setSourceTypeTab] = useState<'screen' | 'window' | 'camera'>(initialTab || 'screen');
  const [isLoadingSources, setIsLoadingSources] = useState(false);

  // Camera Studio State (Record Yourself)
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('default');
  const [isCameraMirrored, setIsCameraMirrored] = useState(false);
  const [cameraPreviewStream, setCameraPreviewStream] = useState<MediaStream | null>(null);
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraAspectRatio, setCameraAspectRatio] = useState<'16:9' | '9:16' | '1:1'>('16:9');
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const isHandingOverStreamRef = useRef<boolean>(false);
  const cameraPreviewStreamRef = useRef<MediaStream | null>(null);

  // Online Meeting Hub State
  const [accountInfo, setAccountInfo] = useState(GoogleService.getAccountInfo());
  const [upcomingMeetings, setUpcomingMeetings] = useState<GoogleMeetEvent[]>(GoogleService.getUpcomingMeetings());
  const [customMeetUrl, setCustomMeetUrl] = useState('');
  const [isMeetArmed, setIsMeetArmed] = useState(false);
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
  const [newMeetingTitle, setNewMeetingTitle] = useState('Online Team Discussion');
  const [newMeetingLink, setNewMeetingLink] = useState('https://meeting.example.com/room-123');
  const [newAttendeeEmail, setNewAttendeeEmail] = useState('guest@gmail.com');
  const [openInAppTool, setOpenInAppTool] = useState(true);

  // Audio setup
  const [audioInputs, setAudioInputs] = useState<AudioDevice[]>([]);
  const [selectedMicId, setSelectedMicId] = useState(settings.defaultMicId);
  const [micEnabled, setMicEnabled] = useState(true);
  const [systemAudioEnabled, setSystemAudioEnabled] = useState(settings.enableSystemAudio);

  // Video Quality
  const [qualityPreset, setQualityPreset] = useState<QualityPreset>(settings.defaultQuality);
  const [cameraOverlayEnabled, setCameraOverlayEnabled] = useState(false);
  const [countdownSec, setCountdownSec] = useState(settings.countdownSeconds);

  // Load capture sources & audio devices
  const refreshSources = async () => {
    setIsLoadingSources(true);
    try {
      const srcList = await recordingEngine.getAvailableSources();
      setSources(srcList);
      if (srcList.length > 0 && !selectedSource) {
        setSelectedSource(srcList[0]);
      }
    } finally {
      setIsLoadingSources(false);
    }
  };

  const refreshCameras = async () => {
    try {
      const camList = await recordingEngine.getCameraDevices();
      setCameras(camList);
      if (camList.length > 0 && (selectedCameraId === 'default' || !camList.some(c => c.deviceId === selectedCameraId))) {
        setSelectedCameraId(camList[0].deviceId);
      }
    } catch (e) {
      console.warn('Could not enumerate cameras:', e);
    }
  };

  useEffect(() => {
    refreshSources();
    refreshCameras();
    audioService.getAudioInputDevices().then(devices => {
      setAudioInputs(devices);
      if (devices.length > 0 && selectedMicId === 'default') {
        setSelectedMicId(devices[0].deviceId);
      }
    });

    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
      const handleDeviceChange = () => {
        refreshCameras();
        audioService.getAudioInputDevices().then(devices => {
          setAudioInputs(devices);
          setSelectedMicId(prev => (devices.some(d => d.deviceId === prev) ? prev : (devices[0]?.deviceId || 'default')));
        });
      };
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      };
    }
  }, []);

  // Update tab if initialTab changes
  useEffect(() => {
    if (initialTab) {
      setSourceTypeTab(initialTab);
    }
  }, [initialTab]);

  // Live Camera Preview lifecycle for "Record Yourself" tab
  useEffect(() => {
    let currentStream: MediaStream | null = null;
    let isCancelled = false;

    if (sourceTypeTab === 'camera') {
      setIsCameraLoading(true);
      setCameraError(null);

      const constraints: MediaStreamConstraints = {
        video: selectedCameraId && selectedCameraId !== 'default'
          ? { deviceId: { exact: selectedCameraId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
          : { width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      };

      navigator.mediaDevices.getUserMedia(constraints)
        .then((stream) => {
          if (isCancelled) {
            stream.getTracks().forEach(t => t.stop());
            return;
          }
          currentStream = stream;
          cameraPreviewStreamRef.current = stream;
          setCameraPreviewStream(stream);
          setIsCameraLoading(false);
        })
        .catch((err) => {
          if (isCancelled) return;
          console.error('Camera preview error:', err);
          setIsCameraLoading(false);
          setCameraError('Webcam access was not granted or camera is busy in another program.');
        });
    } else {
      if (cameraPreviewStreamRef.current) {
        cameraPreviewStreamRef.current.getTracks().forEach(t => t.stop());
        cameraPreviewStreamRef.current = null;
      }
      setCameraPreviewStream(null);
    }

    return () => {
      isCancelled = true;
      if (!isHandingOverStreamRef.current) {
        if (currentStream) {
          currentStream.getTracks().forEach(t => t.stop());
        }
        if (cameraPreviewStreamRef.current) {
          cameraPreviewStreamRef.current.getTracks().forEach(t => t.stop());
          cameraPreviewStreamRef.current = null;
        }
      }
    };
  }, [sourceTypeTab, selectedCameraId]);

  // Bind video element to camera stream
  useEffect(() => {
    if (cameraVideoRef.current && cameraPreviewStream) {
      cameraVideoRef.current.srcObject = cameraPreviewStream;
      cameraVideoRef.current.play().catch(() => {});
    }
  }, [cameraPreviewStream]);

  const filteredSources = sources.filter(s => s.type === sourceTypeTab);

  const handleLaunch = () => {
    const config = QUALITY_PRESETS[qualityPreset];
    if (sourceTypeTab === 'camera') {
      isHandingOverStreamRef.current = true;
      const cam = cameras.find(c => c.deviceId === selectedCameraId);
      const cameraSource: CaptureSource = {
        id: selectedCameraId,
        name: cam?.label || 'Record Yourself (Camera)',
        type: 'camera',
        aspectRatio: cameraAspectRatio,
        cameraMirrored: isCameraMirrored
      };
      onStartRecording(cameraSource, config, cameraPreviewStream);
    } else {
      if (cameraOverlayEnabled) {
        isHandingOverStreamRef.current = true;
      }
      const sourceToRecord: CaptureSource = selectedSource ? {
        ...selectedSource,
        pipEnabled: cameraOverlayEnabled,
        cameraId: cameraOverlayEnabled ? selectedCameraId : undefined,
        cameraMirrored: isCameraMirrored,
        pipPosition: 'bottom-right'
      } : {
        id: 'web_screen_prompt',
        name: 'Screen Recording',
        type: 'screen',
        pipEnabled: cameraOverlayEnabled,
        cameraId: cameraOverlayEnabled ? selectedCameraId : undefined,
        cameraMirrored: isCameraMirrored,
        pipPosition: 'bottom-right'
      };
      onStartRecording(sourceToRecord, config, cameraOverlayEnabled ? cameraPreviewStream : null);
    }
  };

  const handleJoinGoogleMeet = (link: string) => {
    if (!link.trim()) return;
    setIsMeetArmed(true);
    GoogleService.openMeetLink(link.trim(), openInAppTool);
    setSourceTypeTab('window');
    setTimeout(() => {
      refreshSources();
    }, 1500);
  };

  const handleScheduleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!newMeetingLink.trim()) return;

    GoogleService.scheduleMeeting({
      title: newMeetingTitle.trim() || 'Online Discussion',
      meetLink: newMeetingLink.trim(),
      attendees: [
        { name: 'Host', email: accountInfo.email || 'host@gmail.com', role: 'Host' },
        { name: 'Guest', email: newAttendeeEmail.trim() || 'guest@gmail.com', role: 'Guest' }
      ]
    });

    setUpcomingMeetings(GoogleService.getUpcomingMeetings());
    setIsSchedulingOpen(false);
  };

  const handleDeleteMeeting = (id: string) => {
    const updated = GoogleService.deleteScheduledMeeting(id);
    setUpcomingMeetings(updated);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
      {/* Studio Status Bar (Clean, no duplicate header) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-brand-border/60">
        <div className="flex items-center gap-2.5">
          <span className="text-xs font-bold text-brand-text">Capture Engine:</span>
          <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Hardware Video & Audio Loopback Ready
          </span>
        </div>

        {/* Disk Space Status */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-brand-surface border border-brand-border text-xs text-brand-muted">
            <HardDrive className={`w-3.5 h-3.5 ${availableDiskSpaceGb < 15 ? 'text-amber-400' : 'text-emerald-400'}`} />
            <span>Disk Space: <strong className="text-brand-text">{availableDiskSpaceGb} GB</strong> Free</span>
          </div>

          {availableDiskSpaceGb < 10 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/20 text-amber-300 border border-amber-500/40 text-xs">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Low Disk Warning</span>
            </div>
          )}
        </div>
      </div>

      {/* Online Meeting Quick Connect & Scheduled Meetings Hub */}
      <div className="p-5 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-brand-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-600 flex items-center justify-center font-bold">
              <Video className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider">
                  Online Meeting Hub
                </h3>
                {accountInfo.isConnected && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Synced: {accountInfo.email}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-brand-muted">
                {openInAppTool
                  ? 'In-App Tool Mode: Meeting hamare tool ke andar open hogi (Bina naye browser tab ke)'
                  : 'External Browser Mode: Meeting browser tab me open hogi'}
              </p>
            </div>
          </div>

          {/* Hub Action Bar with Mode Switcher */}
          <div className="flex flex-wrap items-center gap-2">
            {/* In-App Tool vs External Tab Toggle */}
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-brand-elevated p-0.5 rounded-xl border border-brand-border text-[11px]">
              <button
                type="button"
                onClick={() => setOpenInAppTool(true)}
                className={`px-2.5 py-1 rounded-lg font-semibold transition-all ${
                  openInAppTool ? 'bg-white dark:bg-brand-surface text-indigo-600 shadow-sm' : 'text-brand-muted hover:text-brand-text'
                }`}
                title="Meeting tool ke andar open hogi (New tab par nahi jayega)"
              >
                In-App (No New Tab)
              </button>
              <button
                type="button"
                onClick={() => setOpenInAppTool(false)}
                className={`px-2.5 py-1 rounded-lg font-semibold transition-all ${
                  !openInAppTool ? 'bg-white dark:bg-brand-surface text-indigo-600 shadow-sm' : 'text-brand-muted hover:text-brand-text'
                }`}
                title="External browser tab me open hoga"
              >
                External Tab
              </button>
            </div>

            <button
              onClick={() => setIsSchedulingOpen(!isSchedulingOpen)}
              className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-brand-text rounded-xl text-xs font-bold transition-all border border-brand-border flex items-center gap-1.5 active:scale-95"
            >
              <Plus className="w-3.5 h-3.5 text-indigo-600" />
              <span>+ Schedule Meeting</span>
            </button>

            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={customMeetUrl}
                onChange={(e) => setCustomMeetUrl(e.target.value)}
                placeholder="Paste Meet URL (e.g. meet.google.com/...)"
                className="w-44 sm:w-52 bg-slate-50 dark:bg-brand-elevated border border-brand-border rounded-xl px-3 py-1.5 text-xs font-mono text-brand-text focus:outline-none"
              />
              <button
                disabled={!customMeetUrl.trim()}
                onClick={() => handleJoinGoogleMeet(customMeetUrl)}
                className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 disabled:opacity-40 active:scale-95"
              >
                <Video className="w-3.5 h-3.5" />
                <span>Join & Record</span>
              </button>
            </div>
          </div>
        </div>

        {/* Inline Schedule Meeting Form Drawer */}
        {isSchedulingOpen && (
          <form onSubmit={handleScheduleSubmit} className="p-4 rounded-2xl bg-indigo-50/60 border border-indigo-200 space-y-3 animate-in fade-in">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-indigo-900 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                Schedule a Meeting Call
              </h4>
              <button
                type="button"
                onClick={() => setIsSchedulingOpen(false)}
                className="text-indigo-600 hover:text-indigo-800 text-xs p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <label className="text-[11px] font-semibold text-brand-muted">Meeting Title</label>
                <input
                  type="text"
                  value={newMeetingTitle}
                  onChange={(e) => setNewMeetingTitle(e.target.value)}
                  placeholder="e.g. Weekly Team Discussion"
                  className="w-full bg-white border border-indigo-200 rounded-lg px-2.5 py-1.5 text-xs text-brand-text focus:outline-none mt-1"
                  required
                />
              </div>

              <div>
                <label className="text-[11px] font-semibold text-brand-muted">Meeting URL</label>
                <input
                  type="text"
                  value={newMeetingLink}
                  onChange={(e) => setNewMeetingLink(e.target.value)}
                  placeholder="https://meeting.example.com/room-123"
                  className="w-full bg-white border border-indigo-200 rounded-lg px-2.5 py-1.5 text-xs font-mono text-brand-text focus:outline-none mt-1"
                  required
                />
              </div>

              <div>
                <label className="text-[11px] font-semibold text-brand-muted">Attendee Email</label>
                <input
                  type="email"
                  value={newAttendeeEmail}
                  onChange={(e) => setNewAttendeeEmail(e.target.value)}
                  placeholder="guest@gmail.com"
                  className="w-full bg-white border border-indigo-200 rounded-lg px-2.5 py-1.5 text-xs font-mono text-brand-text focus:outline-none mt-1"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setIsSchedulingOpen(false)}
                className="px-3 py-1.5 bg-white border border-brand-border text-brand-muted hover:text-brand-text rounded-lg text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm"
              >
                Save & Schedule Meeting
              </button>
            </div>
          </form>
        )}

        {/* Empty State: ONLY shown when NO meetings are scheduled */}
        {upcomingMeetings.length === 0 && !isSchedulingOpen && (
          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-brand-elevated/20 border border-dashed border-brand-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2.5 text-brand-muted">
              <Calendar className="w-4 h-4 text-brand-muted shrink-0" />
              <span>Koi meeting scheduled nahi hai. Meeting ka data tab hi aayega jab meeting schedule hogi.</span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setIsSchedulingOpen(true)}
                className="px-3.5 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>+ Schedule Meeting</span>
              </button>
            </div>
          </div>
        )}

        {/* Scheduled Meetings List: ONLY SHOWN WHEN MEETINGS ARE ACTUALLY SCHEDULED */}
        {upcomingMeetings.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {upcomingMeetings.map((m) => (
              <div
                key={m.id}
                className="p-3.5 rounded-2xl bg-slate-50 dark:bg-brand-elevated/40 border border-indigo-200 dark:border-brand-border flex items-center justify-between gap-3 group hover:border-indigo-400 transition-all shadow-sm"
              >
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <h4 className="text-xs font-bold text-brand-text truncate">{m.title}</h4>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 font-semibold">
                      Scheduled
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-[10px] text-brand-muted">
                    <span className="flex items-center gap-1 font-semibold text-indigo-600">
                      <Users className="w-3 h-3" />
                      {m.attendees.map(a => a.name).join(', ')}
                    </span>
                    <span>•</span>
                    <span className="font-mono text-brand-muted truncate">{m.meetLink}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleJoinGoogleMeet(m.meetLink)}
                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 shrink-0 active:scale-95"
                  >
                    <Video className="w-3.5 h-3.5" />
                    <span>Launch & Arm</span>
                  </button>

                  <button
                    onClick={() => handleDeleteMeeting(m.id)}
                    className="p-2 text-brand-muted hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                    title="Delete scheduled meeting"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {isMeetArmed && (
          <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center justify-between animate-in fade-in">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                {openInAppTool
                  ? 'Meeting hamare tool ke andar open ho chuki hai (Bina kisi naye browser tab ke)! Window select karke "Start Recording" dabayein.'
                  : 'Meeting browser me launch ho gayi hai! Window select karein aur "Start Recording" dabayein.'}
              </span>
            </div>
            <button
              onClick={() => setIsMeetArmed(false)}
              className="text-[11px] text-emerald-700 underline font-normal"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT / CENTER: Capture Source Selection (2 Columns) */}
        <div className="lg:col-span-2 space-y-5">
          {/* Source Tabs */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 bg-brand-surface p-1 rounded-xl border border-brand-border">
              <button
                type="button"
                onClick={() => setSourceTypeTab('screen')}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  sourceTypeTab === 'screen'
                    ? 'bg-brand-accent text-white shadow-glow'
                    : 'text-brand-muted hover:text-brand-text'
                }`}
              >
                <Monitor className="w-3.5 h-3.5" />
                <span>Monitors / Displays</span>
              </button>

              <button
                type="button"
                onClick={() => setSourceTypeTab('window')}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  sourceTypeTab === 'window'
                    ? 'bg-brand-accent text-white shadow-glow'
                    : 'text-brand-muted hover:text-brand-text'
                }`}
              >
                <AppWindow className="w-3.5 h-3.5" />
                <span>Application Windows</span>
              </button>

              <button
                type="button"
                onClick={() => setSourceTypeTab('camera')}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  sourceTypeTab === 'camera'
                    ? 'bg-indigo-600 text-white shadow-glow'
                    : 'text-brand-muted hover:text-brand-text'
                }`}
              >
                <Camera className="w-3.5 h-3.5" />
                <span>Record Yourself</span>
              </button>
            </div>

            {sourceTypeTab !== 'camera' && (
              <button
                type="button"
                onClick={refreshSources}
                className="flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-text p-1.5 rounded-lg bg-brand-surface border border-brand-border transition-colors"
                title="Refresh available windows & screens"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isLoadingSources ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            )}
          </div>

          {/* MAIN CONTENT: RECORD YOURSELF CAMERA STUDIO VS SCREEN SOURCES GRID */}
          {sourceTypeTab === 'camera' ? (
            <div className="p-5 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
              {/* Camera Header & Controls */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-brand-border">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-purple-50 border border-purple-200 text-purple-600 flex items-center justify-center font-bold">
                    <Camera className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
                      Record Yourself Studio
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Webcam Active
                      </span>
                    </h3>
                    <p className="text-[11px] text-brand-muted">
                      Full solo camera video. During recording, click "Share Screen" anytime to present your screen with your camera in a bottom-right circle.
                    </p>
                  </div>
                </div>

                {/* Mirror & Refresh buttons */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsCameraMirrored(!isCameraMirrored)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all ${
                      isCameraMirrored
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700 shadow-sm'
                        : 'bg-slate-50 border-brand-border text-brand-muted hover:text-brand-text'
                    }`}
                    title="Toggle mirror view"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                    <span>{isCameraMirrored ? 'Mirrored (Flipped)' : 'Normal View (No Mirror)'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={refreshCameras}
                    className="p-2 text-brand-muted hover:text-brand-text bg-slate-50 border border-brand-border rounded-xl transition-colors"
                    title="Refresh camera devices"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Live Preview Viewport (Clean Camera Preview) */}
              <div className="space-y-4">
                <div className="w-full flex items-center justify-center p-3 bg-slate-900/50 dark:bg-black/50 rounded-2xl border border-brand-border/60 min-h-[340px]">
                  <div
                    className={`relative bg-black overflow-hidden shadow-2xl flex items-center justify-center transition-all duration-300 ${
                      cameraAspectRatio === '9:16'
                        ? 'w-[230px] aspect-[9/16] rounded-3xl border-4 border-indigo-500 ring-4 ring-indigo-500/20'
                        : 'w-full aspect-video rounded-2xl border border-brand-border'
                    }`}
                  >
                    {/* Smartphone top pill/notch for 9:16 mode */}
                    {cameraAspectRatio === '9:16' && (
                      <div className="absolute top-2.5 w-16 h-3 bg-slate-800 rounded-full z-20 pointer-events-none shadow-sm" />
                    )}

                    {isCameraLoading && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80 bg-black/80 z-10">
                        <RefreshCw className="w-6 h-6 animate-spin text-indigo-400" />
                        <span className="text-xs">Initializing webcam feed...</span>
                      </div>
                    )}

                    {cameraError && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-amber-200 bg-black/90 z-10 space-y-2">
                        <AlertTriangle className="w-8 h-8 text-amber-400" />
                        <p className="text-xs font-medium">{cameraError}</p>
                        <button
                          type="button"
                          onClick={refreshCameras}
                          className="px-4 py-1.5 bg-white text-slate-900 rounded-xl text-xs font-bold shadow transition-all hover:bg-slate-100 cursor-pointer"
                        >
                          Retry Camera Access
                        </button>
                      </div>
                    )}

                    <video
                      ref={cameraVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full h-full object-cover transition-transform ${
                        isCameraMirrored ? 'scale-x-[-1]' : ''
                      }`}
                    />

                    {/* Floating Status Badges on Camera Feed */}
                    <div className="absolute top-3 left-3 flex items-center gap-2 pointer-events-none z-10">
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/75 backdrop-blur-md text-[10px] text-white font-medium">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        {cameraAspectRatio === '9:16' ? '9:16 Shorts View' : 'Live Preview'}
                      </span>
                    </div>

                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none z-10">
                      <span className="px-2 py-0.5 rounded-lg bg-black/75 backdrop-blur-md text-[10px] text-white/90 font-mono truncate max-w-[130px]">
                        {cameras.find(c => c.deviceId === selectedCameraId)?.label || 'Camera'}
                      </span>
                      <span className="px-2 py-0.5 rounded-lg bg-black/75 backdrop-blur-md text-[10px] text-emerald-400 font-mono font-bold">
                        {cameraAspectRatio === '9:16' ? '1080x1920' : '1080p FHD'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Camera Hardware Picker & Aspect Ratio selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div>
                    <label className="text-[11px] font-semibold text-brand-muted block mb-1">Select Camera Device</label>
                    <select
                      value={selectedCameraId}
                      onChange={(e) => setSelectedCameraId(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-brand-elevated border border-brand-border rounded-xl px-3 py-2 text-xs text-brand-text font-medium focus:outline-none"
                    >
                      {cameras.map(c => (
                        <option key={c.deviceId} value={c.deviceId}>{c.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold text-brand-muted block mb-1">Recording Framing Format</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCameraAspectRatio('16:9')}
                        className={`flex-1 py-2 px-2 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                          cameraAspectRatio === '16:9'
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                            : 'bg-slate-50 dark:bg-brand-elevated border-brand-border text-brand-muted hover:text-brand-text'
                        }`}
                      >
                        16:9 Landscape
                      </button>
                      <button
                        type="button"
                        onClick={() => setCameraAspectRatio('9:16')}
                        className={`flex-1 py-2 px-2 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                          cameraAspectRatio === '9:16'
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                            : 'bg-slate-50 dark:bg-brand-elevated border-brand-border text-brand-muted hover:text-brand-text'
                        }`}
                      >
                        📱 9:16 Shorts/Reels
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Sources Grid */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredSources.length === 0 ? (
                <div className="col-span-2 p-8 rounded-2xl bg-brand-surface/40 border border-dashed border-brand-border text-center space-y-2">
                  <Monitor className="w-8 h-8 text-brand-muted mx-auto" />
                  <p className="text-xs text-brand-muted">
                    No {sourceTypeTab === 'screen' ? 'monitors' : 'windows'} detected or running in standard browser mode.
                  </p>
                  <p className="text-[11px] text-brand-secondary">
                    Clicking "Start Recording" will invoke the native system screen picker.
                  </p>
                </div>
              ) : (
                filteredSources.map((source) => {
                  const isSelected = selectedSource?.id === source.id;

                  return (
                    <div
                      key={source.id}
                      onClick={() => setSelectedSource(source)}
                      className={`group relative p-3 rounded-2xl border cursor-pointer transition-all flex flex-col justify-between ${
                        isSelected
                          ? 'bg-brand-surface border-brand-accent shadow-glow'
                          : 'bg-brand-surface/60 hover:bg-brand-surface border-brand-border hover:border-brand-borderLight'
                      }`}
                    >
                      {/* Thumbnail */}
                      <div className="w-full aspect-video rounded-xl bg-black/60 relative overflow-hidden mb-2.5 flex items-center justify-center border border-brand-border/40">
                        {source.thumbnailUrl ? (
                          <img src={source.thumbnailUrl} alt={source.name} className="w-full h-full object-cover" />
                        ) : (
                          <Monitor className="w-8 h-8 text-brand-muted/60" />
                        )}

                        {isSelected && (
                          <div className="absolute top-2 right-2 p-1 rounded-full bg-brand-accent text-white shadow-md">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {source.appIconUrl ? (
                          <img src={source.appIconUrl} alt="" className="w-4 h-4 shrink-0 rounded" />
                        ) : (
                          <Monitor className="w-4 h-4 text-brand-muted shrink-0" />
                        )}
                        <h4 className="text-xs font-semibold text-brand-text truncate group-hover:text-brand-accent transition-colors">
                          {source.name}
                        </h4>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Audio Configuration Section */}
          <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border space-y-4">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-brand-accent" />
              Audio Input & System Loopback Capture
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Mic Input */}
              <div className="p-3 rounded-xl bg-brand-elevated/60 border border-brand-border space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-brand-text flex items-center gap-1.5">
                    <Mic className="w-3.5 h-3.5 text-brand-secondary" />
                    Microphone
                  </label>
                  <input
                    type="checkbox"
                    checked={micEnabled}
                    onChange={(e) => setMicEnabled(e.target.checked)}
                    className="accent-brand-accent w-4 h-4 rounded cursor-pointer"
                  />
                </div>
                <select
                  disabled={!micEnabled}
                  value={selectedMicId}
                  onChange={(e) => setSelectedMicId(e.target.value)}
                  className="w-full bg-brand-surface border border-brand-border rounded-lg px-2.5 py-1.5 text-xs text-brand-text disabled:opacity-50"
                >
                  {audioInputs.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                  ))}
                </select>
              </div>

              {/* System Audio Toggle */}
              <div className="p-3 rounded-xl bg-brand-elevated/60 border border-brand-border space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-brand-text flex items-center gap-1.5">
                    <Volume2 className="w-3.5 h-3.5 text-brand-accent" />
                    Meeting System Audio
                  </label>
                  <input
                    type="checkbox"
                    checked={systemAudioEnabled}
                    onChange={(e) => setSystemAudioEnabled(e.target.checked)}
                    className="accent-brand-accent w-4 h-4 rounded cursor-pointer"
                  />
                </div>
                <p className="text-[11px] text-brand-muted">
                  Captures remote attendee voices via desktop loopback audio.
                </p>
              </div>
            </div>

            {/* Live Audio Meters */}
            <AudioMeter showLabels={true} systemAudioEnabled={systemAudioEnabled} />
          </div>
        </div>

        {/* RIGHT COLUMN: Settings & Launch Studio */}
        <div className="space-y-5">
          {/* Quality & Encoding Preset */}
          <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border space-y-4">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-brand-accent" />
              Recording Quality Preset
            </h3>

            <div className="space-y-2">
              {(Object.keys(QUALITY_PRESETS) as QualityPreset[]).map((key) => {
                const q = QUALITY_PRESETS[key];
                const isSelected = qualityPreset === key;

                return (
                  <div
                    key={key}
                    onClick={() => setQualityPreset(key)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                      isSelected
                        ? 'bg-brand-elevated border-brand-accent shadow-sm'
                        : 'bg-brand-elevated/30 hover:bg-brand-elevated/60 border-brand-border'
                    }`}
                  >
                    <div>
                      <h4 className="text-xs font-semibold text-brand-text">{q.label}</h4>
                      <p className="text-[10px] text-brand-muted font-mono">{q.width}x{q.height} • {q.fps} FPS</p>
                    </div>
                    {isSelected && <CheckCircle2 className="w-4 h-4 text-brand-accent shrink-0" />}
                  </div>
                );
              })}
            </div>

            {/* Options */}
            <div className="pt-2 border-t border-brand-border/60 space-y-2.5 text-xs text-brand-muted">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Camera className="w-3.5 h-3.5" />
                  Webcam Overlay (PiP)
                </span>
                <input
                  type="checkbox"
                  checked={cameraOverlayEnabled}
                  onChange={(e) => setCameraOverlayEnabled(e.target.checked)}
                  className="accent-brand-accent rounded cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Maximize2 className="w-3.5 h-3.5 text-brand-accent" />
                  Auto-Fullscreen on Record
                </span>
                <input
                  type="checkbox"
                  checked={settings.autoFullscreenOnRecord || false}
                  onChange={(e) => {
                    if (window.electronAPI && e.target.checked) {
                      window.electronAPI.setFullScreen(true);
                    }
                  }}
                  className="accent-brand-accent rounded cursor-pointer"
                  title="Make application full screen when recording starts"
                />
              </div>

              <div className="flex items-center justify-between">
                <span>Start Countdown</span>
                <select
                  value={countdownSec}
                  onChange={(e) => setCountdownSec(parseInt(e.target.value, 10))}
                  className="bg-brand-elevated border border-brand-border rounded px-2 py-0.5 text-xs text-brand-text"
                >
                  <option value={0}>None</option>
                  <option value={3}>3 Seconds</option>
                  <option value={5}>5 Seconds</option>
                </select>
              </div>
            </div>
          </div>

          {/* Destination Path Display */}
          <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border space-y-2">
            <span className="text-[11px] font-semibold text-brand-muted flex items-center gap-1.5">
              <Folder className="w-3.5 h-3.5 text-brand-secondary" />
              Recording Output Directory
            </span>
            <p className="text-xs font-mono text-brand-text truncate bg-brand-elevated/50 p-2 rounded-lg border border-brand-border/40">
              {settings.storagePath}
            </p>
          </div>

          {/* Primary Action Button */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleLaunch}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-accent to-brand-accent-hover text-white font-bold text-sm shadow-glow hover:shadow-[0_0_35px_rgba(124,92,252,0.5)] transition-all flex items-center justify-center gap-2 active:scale-98 cursor-pointer"
            >
              <Play className="w-4 h-4 fill-white" />
              <span>
                {sourceTypeTab === 'camera'
                  ? 'Start Recording Yourself'
                  : 'Start Recording'}
              </span>
            </button>
            <p className="text-[10px] text-center text-brand-muted">
              {sourceTypeTab === 'camera'
                ? 'Full solo video recording. Share screen anytime during recording. 100% offline & private.'
                : 'Capture only visible screen pixels. Fully local processing.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
